import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Table, Order, OrderItem, PaymentMethod } from '@/types';
import { 
  Plus, X, ChevronLeft, ArrowLeft, Trash2, Check, Printer, 
  UsersRound, Edit, MessageSquare, Ban, AlertTriangle, CheckCircle,
  CloudUpload, CloudOff, RefreshCw, Percent, MinusCircle, RotateCw,
  CreditCard, FilePlus, CheckSquare, Loader2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import ProductSelector from '@/components/products/ProductSelector';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIsMobile } from '@/hooks/use-mobile';
import PaymentConfirmationDialog from '@/components/payment/PaymentConfirmationDialog';
import TableObservationsDialog from '@/components/tables/TableObservationsDialog';
import TableCancelDialog from '@/components/tables/TableCancelDialog';
import TableHeader from '@/components/tables/TableHeader';
import OrdersList from '@/components/tables/OrdersList';
import { InputNumber } from '@/components/ui/input-number';
import * as z from 'zod';
import { formatCurrency } from '@/lib/utils';
import PedidoPreviewDialog from '@/components/orders/PedidoPreviewDialog';
import { 
  isOrderModifiedSinceLastSync,
  calculateSubtotal,
  calculateDiscountValue,
  calculateOrderTotal,
  calculateTableTotal,
  getOrCreateTableExternalId,
  syncOrderWithErp,
  getPedidoCompleto
} from '@/services/orderApiService';
import { API_CONFIG } from '@/config/apiConfig';

const tableSeatsSchema = z.object({
  seats: z.coerce.number().min(1, {
    message: "A mesa deve ter pelo menos 1 lugar",
  }).max(20, {
    message: "A mesa não pode ter mais de 20 lugares",
  }),
});

type TableSeatsFormValues = z.infer<typeof tableSeatsSchema>;

const cancelReasonSchema = z.object({
  reason: z.string().min(3, {
    message: "A justificativa deve ter pelo menos 3 caracteres",
  }),
});

type CancelReasonFormValues = z.infer<typeof cancelReasonSchema>;

const TableDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    tables, 
    activeTable, 
    setActiveTable, 
    createNewOrder, 
    completeOrder, 
    printOrder, 
    updateTableSeats,
    updateTableObservations,
    cancelTable,
    cancelOrderItem,
    synchronizeOrder,
    getSyncStatusLabel,
    getSyncStatusClass,
    updateItemQuantity,
    applyOrderDiscount,
    finalizeTable,
    refreshData,
    currentUser,
    paymentMethods
  } = useApp();
  
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [seatsDialogOpen, setSeatsDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [observationsDialogOpen, setObservationsDialogOpen] = useState(false);
  const [cancelTableDialogOpen, setCancelTableDialogOpen] = useState(false);
  const [itemToCancel, setItemToCancel] = useState<{orderId: string, itemId: string} | null>(null);
  const [cancelItemDialogOpen, setCancelItemDialogOpen] = useState(false);
  const [finalizeTableDialogOpen, setFinalizeTableDialogOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isClosingOrder, setIsClosingOrder] = useState(false);
  const [pedidoPreviewOpen, setPedidoPreviewOpen] = useState(false);
  const [pedidoPreview, setPedidoPreview] = useState<any | null>(null);
  const [pedidoPreviewLoading, setPedidoPreviewLoading] = useState(false);
  const [pedidoPreviewError, setPedidoPreviewError] = useState<string | null>(null);
  const [navigateAfterPreview, setNavigateAfterPreview] = useState(false);
  const [pedidoPreviewQueue, setPedidoPreviewQueue] = useState<number[]>([]);
  const isMobile = useIsMobile();

  const form = useForm<TableSeatsFormValues>({
    resolver: zodResolver(tableSeatsSchema),
    defaultValues: {
      seats: activeTable?.seats || 2,
    },
  });

  const cancelItemForm = useForm<CancelReasonFormValues>({
    resolver: zodResolver(cancelReasonSchema),
    defaultValues: {
      reason: "",
    },
  });
  
  useEffect(() => {
    if (activeTable) {
      form.setValue('seats', activeTable.seats);
    }
  }, [activeTable, form]);
  
  useEffect(() => {
    if (!id) return;
    
    const table = tables.find(t => t.id === id);
    if (table) {
      setActiveTable(table);
      
      const openOrders = table.orders.filter(o => o.status === 'open');
      if (openOrders.length > 0) {
        const sortedOrders = [...openOrders].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setActiveOrderId(sortedOrders[0].id);
      }
    } else {
      navigate('/tables');
    }
    
    return () => {
      setActiveTable(null);
    };
  }, [id, tables, setActiveTable, navigate, createNewOrder]);
  
  if (!activeTable) {
    return <div className="text-center py-10">Carregando mesa...</div>;
  }

  const handleCreateOrder = () => {
    if (!activeTable) return;
    
    setIsCreatingOrder(true);
    
    try {
      getOrCreateTableExternalId(activeTable.id);
      
      const newOrder = createNewOrder(activeTable.id, 'dine-in');
      
      setActiveOrderId(newOrder.id);
      
      setProductDialogOpen(true);
      
      toast({
        title: "Novo pedido criado",
        description: `Pedido #${newOrder.id.substring(0, 8)} criado com sucesso`,
      });
    } catch (error) {
      console.error("Erro ao criar pedido:", error);
      toast({
        title: "Erro ao criar pedido",
        description: "Não foi possível criar um novo pedido",
        variant: "destructive"
      });
    } finally {
      setIsCreatingOrder(false);
    }
  };
  
  const handleSelectOrder = (orderId: string) => {
    setActiveOrderId(orderId);
  };

  const handleUpdateQuantity = async (orderId: string, itemId: string, quantity: number) => {
    if (updateItemQuantity) {
      updateItemQuantity(orderId, itemId, quantity);
    }

    // NÃO fazer auto-sync aqui. Dentro da mesa o estado é local e a sincronização deve ser explícita
    // (botão "Sincronizar"), para evitar sobrescrever o pedido com retornos do ERP.
  };

  const handleAddItems = (orderId: string) => {
    setActiveOrderId(orderId);
    setProductDialogOpen(true);
  };

  const handleSendToKitchen = (orderId: string) => {
    // Enviar para cozinha - implementar conforme necessário
    toast({
      title: "Pedido enviado para cozinha",
      description: `Pedido #${orderId.substring(0, 8)} foi enviado para a cozinha`,
    });
  };

  const handlePrintOrder = async (orderId: string) => {
    if (!orderId) return;
    
    setIsPrinting(true);
    try {
      await printOrder(orderId);
      toast({
        title: "Pedido impresso",
        description: `O pedido foi enviado para impressão.`,
      });
    } catch (error) {
      console.error("Erro ao imprimir:", error);
      toast({
        title: "Erro na impressão",
        description: "Não foi possível imprimir o pedido.",
        variant: "destructive"
      });
    } finally {
      setIsPrinting(false);
    }
  };

  const handleCancelItem = (orderId: string, itemId: string) => {
    setItemToCancel({ orderId, itemId });
    setCancelItemDialogOpen(true);
  };

  const handleConfirmCancelItem = async (values: CancelReasonFormValues) => {
    if (itemToCancel && cancelOrderItem) {
      cancelOrderItem(itemToCancel.orderId, itemToCancel.itemId, values.reason);
      cancelItemForm.reset();
      setCancelItemDialogOpen(false);

      // NÃO fazer auto-sync aqui.
      // Sincronização deve ser explícita (botão "Sincronizar"), para não sobrescrever estado local.
    }
  };

  const handleNavigateBack = () => {
    navigate('/tables');
  };

  const handleEditSeats = () => {
    setSeatsDialogOpen(true);
  };

  const handleEditObservations = () => {
    setObservationsDialogOpen(true);
  };

  const handleUpdateSeats = (values: TableSeatsFormValues) => {
    if (updateTableSeats && activeTable) {
      updateTableSeats(activeTable.id, values.seats);
      setSeatsDialogOpen(false);
      toast({
        title: "Lugares atualizados",
        description: `A mesa ${activeTable.number} agora tem ${values.seats} lugares`,
      });
    }
  };

  const handleSaveObservations = (observations: string) => {
    if (updateTableObservations && activeTable) {
      updateTableObservations(activeTable.id, observations);
      setObservationsDialogOpen(false);
      toast({
        title: "Observações salvas",
        description: "As observações da mesa foram atualizadas com sucesso",
      });
    }
  };

  const handleCancelTable = (reason: string) => {
    if (cancelTable && activeTable) {
      cancelTable(activeTable.id, reason);
      setCancelTableDialogOpen(false);
      toast({
        title: "Mesa cancelada",
        description: `A mesa ${activeTable.number} foi cancelada`,
      });
      navigate('/tables');
    }
  };

  const handleSyncTable = async () => {
    if (!activeTable) return;

    try {
      setIsProcessingPayment(true);

      // Inicializa a API e sincroniza pedidos abertos e pagos pendentes
      API_CONFIG.initialize();

      const ordersToSync = activeTable.orders.filter(order =>
        order.status === 'open' || (order.status === 'paid' && (!order.syncStatus || order.syncStatus === 'pending'))
      );

      if (ordersToSync.length === 0) {
        toast({
          title: "Nada para sincronizar",
          description: "Nenhum pedido com alterações para enviar",
        });
        return;
      }

      let syncedCount = 0;

      for (const order of ordersToSync) {
        try {
          const clienteID = order.customerId ? parseInt(order.customerId, 10) : 0;
          console.log('[Mesas] Sincronizando pedido:', order.id, 'status:', order.status);

          const syncResult = await syncOrderWithErp(
            order,
            paymentMethods,
            [],
            currentUser?.vendedor_id ? Number(currentUser.vendedor_id) : 1,
            clienteID,
            false
          );

          if (syncResult > 0) {
            syncedCount++;
            if (order.status === 'paid') {
              // Marca como sincronizado somente pedidos pagos
              await synchronizeOrder(order.id, true);
            }
          }
        } catch (syncError) {
          console.error(`[Mesas] Erro ao sincronizar pedido ${order.id}:`, syncError);
          toast({
            title: "Erro de sincronização",
            description: `Erro ao sincronizar pedido ${order.id}`,
            variant: "destructive"
          });
        }
      }

      if (syncedCount > 0) {
        toast({
          title: "Sincronização concluída",
          description: `${syncedCount} pedido(s) sincronizado(s) com sucesso`,
        });
        // Não chamar refreshData aqui (pode disparar fluxo que consulta MesasAbertas).
        // A atualização visual da mesa deve vir do estado local.
      }

    } catch (error) {
      console.error('[Mesas] Erro ao sincronizar mesa:', error);
      toast({
        title: "Erro na sincronização",
        description: "Houve um erro ao sincronizar a mesa. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleFinalizeTable = () => {
    const openOrders = activeTable?.orders.filter(o => o.status === 'open') || [];
    const hasAnyOrders = (activeTable?.orders.filter(o => 
      o.status === 'open' || o.status === 'paid'
    ) || []).length > 0;
    
    if (hasAnyOrders) {
      const hasItems = activeTable?.orders.some(order => 
        (order.status === 'open' || order.status === 'paid') && order.items.length > 0
      );
      
      if (hasItems) {
        setFinalizeTableDialogOpen(true);
      } else {
        toast({
          title: "Nenhum item nos pedidos",
          description: "Adicione itens antes de finalizar a mesa.",
          variant: "destructive"
        });
      }
    } else {
      toast({
        title: "Nenhum pedido na mesa",
        description: "Crie um pedido antes de finalizar a mesa.",
        variant: "destructive"
      });
    }
  };

  const handleOpenPedidoPreview = async (vendaId: number) => {
    setPedidoPreviewOpen(true);
    setPedidoPreviewLoading(true);
    setPedidoPreviewError(null);
    setPedidoPreview(null);
    try {
      const pedido = await getPedidoCompleto(vendaId);
      setPedidoPreview(pedido as any);
    } catch (err) {
      setPedidoPreviewError(err instanceof Error ? err.message : 'Não foi possível carregar o pedido.');
    } finally {
      setPedidoPreviewLoading(false);
    }
  };

  const handleConfirmFinalizeTable = async (
    order: Order | undefined, 
    paymentMethods: PaymentMethod[], 
    paymentValues: number[]
  ) => {
    if (!activeTable || !finalizeTable) return;
    
    try {
      setIsFinalizing(true);
      
      const result = await finalizeTable(activeTable.id, paymentMethods, paymentValues);
      
      if (result.success) {
        toast({
          title: "Mesa finalizada",
          description: `Mesa ${activeTable.number} foi finalizada com sucesso.`,
        });
        
        setFinalizeTableDialogOpen(false);
        setActiveOrderId(null);

        const vendaIds = Array.isArray(result.vendaIds)
          ? result.vendaIds.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
          : [];

        // Abrir pré-visualização (como no PDV) para o usuário imprimir/compartilhar pelo navegador
        const vendaIdToPreview = vendaIds.length ? vendaIds[0] : null;

        setPedidoPreviewQueue(vendaIds.length > 1 ? vendaIds.slice(1) : []);

        if (vendaIdToPreview) {
          setNavigateAfterPreview(true);
          await handleOpenPedidoPreview(vendaIdToPreview);
        } else {
          setTimeout(() => {
            navigate('/tables');
          }, 300);
        }
      } else {
        toast({
          title: "Problema na finalização",
          description: "A mesa não pôde ser finalizada. Verifique os dados e tente novamente.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Erro ao finalizar mesa:", error);
      toast({
        title: "Erro ao finalizar mesa",
        description: "Ocorreu um erro ao tentar finalizar a mesa.",
        variant: "destructive"
      });
    } finally {
      setIsFinalizing(false);
    }
  };

  const calculateTotalTableAmount = (): number => {
    if (!activeTable) return 0;
    
    const relevantOrders = activeTable.orders.filter(o => 
      o.status === 'open' || o.status === 'paid'
    );
    return calculateTableTotal(relevantOrders);
  };

  return (
    <div className="container py-4 space-y-6">
      {/* Header da Mesa */}
          <TableHeader
            table={activeTable}
            onNavigateBack={handleNavigateBack}
            onEditSeats={handleEditSeats}
            onEditObservations={handleEditObservations}
            onSyncTable={handleSyncTable}
          />

      {/* Lista de Pedidos */}
      <OrdersList 
        orders={activeTable.orders}
        activeOrderId={activeOrderId}
        onSelectOrder={handleSelectOrder}
        onCreateOrder={handleCreateOrder}
        onUpdateQuantity={handleUpdateQuantity}
        onAddItems={handleAddItems}
        onSendToKitchen={handleSendToKitchen}
        onPrintOrder={handlePrintOrder}
        onCancelItem={handleCancelItem}
        isCreatingOrder={isCreatingOrder}
      />

      {/* Botão Finalizar Mesa */}
      {activeTable.orders.length > 0 && (
        <div className="flex justify-end">
          <Button 
            onClick={handleFinalizeTable}
            disabled={calculateTotalTableAmount() <= 0 || isFinalizing}
            className="bg-blue-500 hover:bg-blue-600"
            size="lg"
          >
            {isFinalizing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Finalizando Mesa...
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4 mr-2" />
                Finalizar Mesa - {formatCurrency(calculateTotalTableAmount())}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Diálogos */}
      <Dialog open={seatsDialogOpen} onOpenChange={setSeatsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Editar número de lugares</DialogTitle>
            <DialogDescription>
              Defina quantos lugares a mesa {activeTable.number} possui.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleUpdateSeats)} className="space-y-4 py-2">
              <FormField
                control={form.control}
                name="seats"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número de lugares</FormLabel>
                    <FormControl>
                      <InputNumber
                        min={1}
                        max={20}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSeatsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">Salvar alterações</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <TableObservationsDialog
        tableId={activeTable.id}
        observations={activeTable.observations || ''}
        open={observationsDialogOpen}
        onOpenChange={setObservationsDialogOpen}
        onSave={handleSaveObservations}
      />
      
      <TableCancelDialog
        tableId={activeTable.id}
        open={cancelTableDialogOpen}
        onOpenChange={setCancelTableDialogOpen}
        onConfirm={handleCancelTable}
      />

      <Dialog open={cancelItemDialogOpen} onOpenChange={setCancelItemDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Cancelar item do pedido</DialogTitle>
            <DialogDescription>
              Informe o motivo do cancelamento deste item.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...cancelItemForm}>
            <form onSubmit={cancelItemForm.handleSubmit(handleConfirmCancelItem)} className="space-y-4 py-2">
              <FormField
                control={cancelItemForm.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Motivo do cancelamento</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Informe o motivo do cancelamento..." 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCancelItemDialogOpen(false)}>
                  Voltar
                </Button>
                <Button type="submit" variant="destructive">
                  Cancelar Item
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <PaymentConfirmationDialog
        open={finalizeTableDialogOpen}
        onOpenChange={setFinalizeTableDialogOpen}
        onConfirm={handleConfirmFinalizeTable}
        isProcessing={isFinalizing}
        totalAmount={calculateTotalTableAmount()}
        defaultPeopleCount={activeTable?.seats}
         title="Finalizar Mesa"
        description={`Finalize a mesa ${activeTable?.number} com um total de ${formatCurrency(calculateTotalTableAmount())}`}
      />

      <PedidoPreviewDialog
        open={pedidoPreviewOpen}
        onOpenChange={(open) => {
          setPedidoPreviewOpen(open);

          if (!open) {
            if (pedidoPreviewQueue.length > 0) {
              const [nextVendaId, ...rest] = pedidoPreviewQueue;
              setPedidoPreviewQueue(rest);
              void handleOpenPedidoPreview(nextVendaId);
              return;
            }

            if (navigateAfterPreview) {
              setNavigateAfterPreview(false);
              navigate('/tables');
            }
          }
        }}
        pedido={pedidoPreview}
        isLoading={pedidoPreviewLoading}
        error={pedidoPreviewError}
        tableNumber={activeTable?.number}
      />
      
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="sm:max-w-[600px] w-[96%] h-[80vh] max-h-[600px] overflow-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Item ao Pedido</DialogTitle>
            <DialogDescription>
              Selecione os produtos para adicionar ao pedido
            </DialogDescription>
          </DialogHeader>
          
          <ProductSelector 
            orderId={activeOrderId || undefined} 
            onClose={() => setProductDialogOpen(false)}
          />
          
          <DialogFooter className="pt-2">
            <Button onClick={() => setProductDialogOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TableDetailPage;